// Puente WebRTC del navegador ↔ Asterisk (SIP sobre WSS + DTLS-SRTP) usando SIP.js.
// El navegador (operador) registra su PROPIA identidad WebRTC (nunca el secreto del HM) y llama
// a la extensión del HM que el backend autorizó para este caso. Solo control de señalización y
// media de audio; ninguna credencial ni término SIP se muestra en la UI.
import { UserAgent, Registerer, Inviter, SessionState } from "sip.js";

// cfg = { wss_url, server, auth_user, password, target }
// cbs = { onState(state), remoteAudio(HTMLAudioElement) }
export function createAudioBridge(cfg, cbs) {
  const onStateRaw = cbs.onState || (() => {});
  let ua = null, registerer = null, inviter = null, stopped = false, parada = null;

  // GC105 · ningún evento del puente se emite después de pedir su parada: el ciclo terminal lo
  // gobierna quien llamó a stop(), no una notificación tardía de SIP.js.
  function onState(st) {
    if (stopped) return;
    onStateRaw(st);
  }

  function attachRemote(session) {
    const pc = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc || !cbs.remoteAudio) return;
    const stream = new MediaStream();
    pc.getReceivers().forEach((r) => { if (r.track) stream.addTrack(r.track); });
    pc.addEventListener?.("track", (e) => { if (e.track) stream.addTrack(e.track); });
    try { cbs.remoteAudio.srcObject = stream; cbs.remoteAudio.play?.().catch(() => {}); } catch (_) {}
  }

  // Suelta TODO lo que exista en este momento (llamada, registro y transporte). Tolerante a que
  // algún paso aún no se hubiera creado: se vuelve a invocar si el arranque creó algo después.
  async function cerrarRecursos() {
    try {
      if (inviter) {
        if (inviter.state === SessionState.Established) await inviter.bye();
        else if (inviter.state === SessionState.Establishing || inviter.state === SessionState.Initial) inviter.cancel();
      }
    } catch (_) {}
    try { if (registerer) await registerer.unregister(); } catch (_) {}
    try { if (ua) await ua.stop(); } catch (_) {}
  }

  async function start() {
    onState("connecting");
    const uri = UserAgent.makeURI(`sip:${cfg.auth_user}@${cfg.server}`);
    ua = new UserAgent({
      uri,
      transportOptions: { server: cfg.wss_url },
      authorizationUsername: cfg.auth_user,
      authorizationPassword: cfg.password,
      logLevel: "error",
      delegate: {
        onDisconnect: () => { onState("error:red"); },
      },
    });
    await ua.start();
    // GC105-C · tras CADA paso asíncrono se comprueba la parada: si se pidió mientras el paso
    // estaba en vuelo, se sueltan los recursos recién creados y NO se origina ninguna llamada.
    if (stopped) return cerrarRecursos();

    registerer = new Registerer(ua);
    await registerer.register();
    if (stopped) return cerrarRecursos();

    const target = UserAgent.makeURI(`sip:${cfg.target}@${cfg.server}`);
    inviter = new Inviter(ua, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    inviter.stateChange.addListener((st) => {
      if (st === SessionState.Established) { attachRemote(inviter); onState("active"); }
      // Terminated SIN parada local = el dispositivo colgó o la señalización cayó. Se notifica UNA
      // vez: el consumidor pedirá entonces la liberación de la reserva en el backend.
      else if (st === SessionState.Terminated) { onState("ended"); }
    });
    if (stopped) return cerrarRecursos();   // la llamada NO llega a originarse

    await inviter.invite();
    if (stopped) return cerrarRecursos();   // el INVITE salió justo antes: se cancela/cuelga
  }

  // IDEMPOTENTE: varias llamadas comparten la misma parada (sin segundo BYE ni segundo unregister).
  function stop() {
    if (parada) return parada;
    stopped = true;
    parada = cerrarRecursos();
    return parada;
  }

  return { start, stop };
}
